<template>
  <div>
    <div class="vgt-loading vgt-center-align" v-if="loading">
      <span class="vgt-loading__content">Loading...</span>
    </div>

    <ul class="nav nav-tabs ig-tabs">
      <li class="nav-item" v-for="tab in tabs" :key="tab.section">
        <router-link
          exact
          :to="tab.path"
          class="nav-link"
          :class="{ active: selected === tab.section }"
        >
          {{ tab.label }}
        </router-link>
      </li>
    </ul>

    <div
      class="card"
      v-for="tab in tabs"
      :key="'tab-' + tab.section"
      v-show="selected === tab.section"
    >
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Instagram — Post in griglia (max 12)</h5>
        <button class="btn btn-success" @click="save(tab.section)" :disabled="saving">
          {{ saving ? 'Salvataggio...' : 'Salva' }}
        </button>
      </div>

      <div class="card-body">
        <p class="text-muted">
          Aggiungi fino a 12 URL di post Instagram. Clicca <strong>Fetch</strong> per
          recuperare automaticamente immagine e didascalia.
          <br />
          Shortcode da inserire nella pagina: <code>{{ tab.shortcode }}</code>
        </p>

        <div
          v-for="(post, index) in posts[tab.section]"
          :key="index"
          class="ig-row mb-3 p-3 border rounded"
        >
          <div class="row align-items-center">
            <!-- Anteprima immagine -->
            <div class="col-md-2 text-center">
              <img
                v-if="post.image"
                :src="post.image"
                class="ig-preview"
                alt="preview"
              />
              <div v-else class="ig-placeholder">No image</div>
            </div>

            <!-- URL + caption -->
            <div class="col-md-8">
              <div class="form-group mb-2">
                <label>URL Instagram</label>
                <div class="input-group">
                  <input
                    type="url"
                    class="form-control"
                    v-model="post.url"
                    placeholder="https://www.instagram.com/p/..."
                  />
                  <div class="input-group-append">
                    <button
                      class="btn btn-outline-primary"
                      @click="fetchPost(post)"
                      :disabled="post.fetching"
                    >
                      {{ post.fetching ? 'Recupero...' : 'Recupera' }}
                    </button>
                  </div>
                </div>
                <small v-if="post.error" class="text-danger">{{ post.error }}</small>
              </div>

              <div class="form-group mb-0">
                <label>Immagine (URL)</label>
                <input type="text" class="form-control mb-1" v-model="post.image" />
                <label>Didascalia</label>
                <textarea class="form-control" rows="2" v-model="post.caption"></textarea>
              </div>
            </div>

            <!-- Rimuovi -->
            <div class="col-md-2 text-center">
              <button class="btn btn-danger btn-sm" @click="removePost(tab.section, index)">
                Rimuovi
              </button>
            </div>
          </div>
        </div>

        <button
          class="btn btn-outline-primary mt-2"
          @click="addPost(tab.section)"
          :disabled="posts[tab.section].length >= 12"
        >
          + Aggiungi post
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

// Ogni tab è una griglia indipendente salvata in una sezione site_options diversa,
// raggiungibile da url: #/instagram-settings (griglia 1) e #/instagram-settings/2
const TABS = [
  { griglia: "1", path: "/instagram-settings", section: "instagram_posts", label: "Griglia 1", shortcode: "[instagram]" },
  { griglia: "2", path: "/instagram-settings/2", section: "instagram_posts_2", label: "Griglia 2", shortcode: '[instagram set="2"]' },
];

export default {
  data() {
    return {
      loading: true,
      saving: false,
      tabs: TABS,
      posts: TABS.reduce((acc, tab) => ({ ...acc, [tab.section]: [] }), {}),
    };
  },

  computed: {
    selected() {
      const griglia = String(this.$route.params.griglia || "1");
      const tab = this.tabs.find((t) => t.griglia === griglia) || this.tabs[0];
      return tab.section;
    },
  },

  created: async function () {
    if ((await this.$api.user.can("home-settings")) == false) {
      this.$router.replace("/not-found");
      return;
    }
    await this.loadData();
  },

  methods: {
    emptyPost() {
      return { url: "", image: "", caption: "", type: "image", media: "", fetching: false, error: "" };
    },

    addPost(section) {
      if (this.posts[section].length < 12) {
        this.posts[section].push(this.emptyPost());
      }
    },

    removePost(section, index) {
      this.posts[section].splice(index, 1);
    },

    async fetchPost(post) {
      if (!post.url) return;

      post.fetching = true;
      post.error = "";

      try {
        const response = await axios({
          method: "get",
          url: this.$url + "operations/instagram/fetch",
          headers: this.$config.axiosHeaders(),
          params: { url: post.url },
        });
        post.image   = response.data.image   || post.image;
        post.caption = response.data.caption || post.caption;
      } catch (e) {
        post.error = e.response?.data?.error || "Fetch fallito. Inserisci URL immagine manualmente.";
      } finally {
        post.fetching = false;
      }
    },

    async loadData() {
      this.loading = true;
      try {
        const response = await axios({
          method: "get",
          url: this.$url + "operations/settings",
          headers: this.$config.axiosHeaders(),
          params: { section: this.tabs.map((tab) => tab.section).join(",") },
        });
        this.tabs.forEach((tab) => {
          const raw = response.data?.[tab.section]?.all?.posts;
          this.posts[tab.section] = Array.isArray(raw)
            ? raw.map((p) => ({ ...this.emptyPost(), ...p }))
            : [];
        });
      } catch (e) {
        this.tabs.forEach((tab) => (this.posts[tab.section] = []));
      } finally {
        this.loading = false;
      }
    },

    async save(section) {
      this.saving = true;
      const clean = this.posts[section].map(({ fetching, error, ...p }) => p);
      try {
        await axios({
          method: "post",
          url: this.$url + "operations/settings",
          headers: this.$config.axiosHeaders(),
          data: {
            section: section,
            data: { all: { posts: clean } },
          },
        });
        this.$notifyVue("top", "center", "success", "Instagram salvato", "ti-check");
      } catch (e) {
        this.$notifyVue("top", "center", "danger", "Errore nel salvataggio", "ti-close");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ig-tabs {
  border-bottom: none;
}
.ig-tabs .nav-link.active {
  font-weight: bold;
}
.ig-preview {
  width: 100%;
  max-height: 100px;
  object-fit: cover;
  border-radius: 4px;
}
.ig-placeholder {
  width: 100%;
  height: 80px;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;
  border-radius: 4px;
}
.ig-row {
  background: #fafafa;
}
</style>
